import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Logo from "@/components/Logo";
import { Award, ShieldCheck, ShieldX, Search, Loader2 } from "lucide-react";

export default function Verify() {
  const [params, setParams] = useSearchParams();
  const [id, setId] = useState(params.get("id") || "");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const check = async (cid) => {
    const value = (cid || id).trim();
    if (!value) return;
    setBusy(true); setResult(null);
    try {
      const { data } = await axios.get(`${API}/certificates/verify/${encodeURIComponent(value)}`);
      setResult(data); setParams({ id: value });
    } catch { setResult({ valid: false, certificate_id: value, error: true }); }
    finally { setBusy(false); }
  };

  useEffect(() => { if (params.get("id")) check(params.get("id")); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-[#F5F9FE] py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="flex items-center gap-2 mb-6"><Logo size={40} /><span className="font-display font-extrabold text-[#0A2A5E] text-xl">LUMIS HR</span></Link>
        <Card className="p-6 sm:p-10 bg-white border-slate-200">
          <div className="text-xs uppercase tracking-[0.2em] text-[#C7761A] font-bold">Certificate verification</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0A2A5E] mt-1">Verify a LUMIS AI Certified credential</h1>
          <p className="text-sm text-slate-600 mt-2">Enter the certificate ID printed on the certificate (format <span className="font-mono">LAI-XXXXXXXX</span>) to confirm it was issued by the LUMIS HR App.</p>
          <form onSubmit={(e) => { e.preventDefault(); check(); }} className="flex gap-2 mt-5">
            <Input data-testid="verify-input" value={id} onChange={(e) => setId(e.target.value.toUpperCase())} placeholder="LAI-7932C15A" className="font-mono h-11" />
            <Button data-testid="verify-btn" type="submit" disabled={busy || !id.trim()} className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white h-11">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-2" /> Verify</>}</Button>
          </form>

          {result && result.valid && (
            <div data-testid="verify-valid" className="mt-6 p-5 rounded-xl bg-emerald-50 border border-emerald-200">
              <div className="flex items-center gap-2 text-emerald-800 font-bold"><ShieldCheck className="w-5 h-5" /> Genuine certificate</div>
              <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
                <div><div className="text-xs text-slate-500">Holder</div><div className="font-semibold text-[#0A2A5E]">{result.holder}</div></div>
                <div><div className="text-xs text-slate-500">Certificate ID</div><div className="font-mono font-semibold">{result.certificate_id}</div></div>
                <div><div className="text-xs text-slate-500">Earned</div><div>{new Date(result.earned_at).toLocaleDateString()}</div></div>
                <div><div className="text-xs text-slate-500">Issuer</div><div>{result.issuer}</div></div>
              </div>
              <div className="mt-3 text-xs text-slate-600">{result.program}</div>
              <ul className="mt-3 space-y-1">
                {result.modules.map((m) => <li key={m.title} className="text-sm flex items-center gap-2 text-slate-700"><Award className="w-4 h-4 text-[#C7761A]" /> {m.title} <span className="ml-auto font-semibold text-[#0A2A5E]">{m.score != null ? `${m.score}%` : "—"}</span></li>)}
              </ul>
            </div>
          )}
          {result && !result.valid && (
            <div data-testid="verify-invalid" className="mt-6 p-5 rounded-xl bg-rose-50 border border-rose-200 flex items-center gap-2 text-rose-800 font-semibold"><ShieldX className="w-5 h-5" /> No certificate found for <span className="font-mono">{result.certificate_id}</span>.</div>
          )}
          <p className="text-[11px] text-slate-400 mt-8 border-t border-slate-100 pt-3">© 2026 LUMIS HR Solutions LLC. LUMIS HR and LUMIS AI™ are products and intellectual property of LUMIS HR Solutions LLC. Verification confirms issuance by the LUMIS HR App only; it is not an employment or legal credential.</p>
        </Card>
      </div>
    </div>
  );
}
